import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Upload,
  Video,
  Image as ImageIcon,
  X,
  Loader2,
  FolderOpen,
  Volume2,
  Music,
  Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const mediaKey = (item) => `${item.type || 'media'}:${item.url || item.id}`;

const mergeMediaItems = (baseItems, incomingItems) => {
  const seen = new Set();
  return [...(incomingItems || []), ...(baseItems || [])].filter((item) => {
    if (!item?.url) return false;
    const key = mediaKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const formatOriginLabel = (item) => {
  if (item.source === 'inhouse_stock') {
    const bits = [item.kind === 'sfx' ? 'Stock SFX' : 'Stock music'];
    if (item.mood) bits.push(item.mood);
    else if (item.tone) bits.push(item.tone);
    return bits.join(' · ');
  }
  if (item.source === 'final') return 'Final render';
  if (item.source === 'background_music') return 'BG music';
  if (item.source === 'upload') return 'Upload';
  if (item.source === 'history') return 'Scene history';
  if (item.source === 'current') return 'Scene';
  if (item.sceneId) return 'Scene';
  return item.source ? String(item.source) : '—';
};

const dragHint = (item) => {
  if (item.type === 'music' || item.source === 'background_music' || item.kind === 'bg_music') {
    return 'Drag to music lane';
  }
  if (item.type === 'audio' || item.kind === 'sfx') return 'Drag to SFX / audio lane';
  return 'Drag to timeline';
};

function itemSearchBlob(item) {
  return [
    item.name,
    item.title,
    item.prompt,
    item.tone,
    item.mood,
    item.genre,
    ...(item.tags || []),
  ].filter(Boolean).join(' ').toLowerCase();
}

function itemPassesFilters(item, typeFilter, originFilter, search) {
  const q = (search || '').trim().toLowerCase();
  if (q && !itemSearchBlob(item).includes(q)) return false;

  if (typeFilter !== 'all') {
    if (typeFilter === 'music') {
      if (item.type !== 'music' && item.source !== 'background_music' && item.kind !== 'bg_music') {
        return false;
      }
    } else if (typeFilter === 'audio') {
      if (item.type !== 'audio' && item.kind !== 'sfx') return false;
    } else if (item.type !== typeFilter) return false;
  }

  if (originFilter === 'all') return true;
  if (originFilter === 'inhouse_stock') return item.source === 'inhouse_stock';
  if (originFilter === 'final') return item.source === 'final';
  if (originFilter === 'background_music') {
    return item.source === 'background_music' || item.type === 'music' || item.kind === 'bg_music';
  }
  if (originFilter === 'upload') return item.source === 'upload';
  if (originFilter === 'scene') {
    return Boolean(item.sceneId) || item.source === 'current' || item.source === 'history';
  }
  return true;
}

function normalizeStockItem(row) {
  if (!row?.url) return null;
  const kind = row.kind === 'sfx' ? 'sfx' : 'bg_music';
  return {
    ...row,
    id: row.id || `stock-${row.media_asset_id || row.url}`,
    name: row.name || row.title || (kind === 'sfx' ? 'Stock SFX' : 'Stock music'),
    type: kind === 'sfx' ? 'audio' : 'music',
    kind,
    source: 'inhouse_stock',
    duration: Number(row.duration_seconds) || Number(row.duration) || 8,
    volume: kind === 'sfx' ? 0.35 : 0.22,
  };
}

export function MediaLibrary({ onMediaSelect, initialItems = [], onClose = null }) {
  const [mediaItems, setMediaItems] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const allItems = useMemo(
    () => mergeMediaItems(mediaItems, stockItems),
    [mediaItems, stockItems],
  );

  const filteredItems = useMemo(
    () => allItems.filter((item) =>
      itemPassesFilters(item, typeFilter, originFilter, searchQuery)),
    [allItems, typeFilter, originFilter, searchQuery],
  );

  useEffect(() => {
    setMediaItems((prev) => mergeMediaItems(prev, initialItems));
  }, [initialItems]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStockLoading(true);
      try {
        const { data } = await api.get('/stock-audio', {
          params: { kind: 'all', limit: 100 },
        });
        if (cancelled) return;
        const rows = (data?.items || []).map(normalizeStockItem).filter(Boolean);
        setStockItems(rows);
      } catch {
        if (!cancelled) setStockItems([]);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((f) =>
      f.type.startsWith('video/') || f.type.startsWith('image/') || f.type.startsWith('audio/'));

    if (validFiles.length === 0) {
      toast.error('Please upload video, image, or audio files');
      return;
    }

    setUploading(true);

    for (const file of validFiles) {
      try {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        toast.info(`Uploading ${file.name}...`);

        const { data: presignedData } = await api.post('/media/presigned-url', null, {
          params: { filename: file.name, content_type: file.type },
        });

        const uploadUrl = presignedData.upload_url || presignedData.presigned_url;
        const viewUrl = presignedData.view_url || presignedData.s3_url;

        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        const newItem = {
          id: `media-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name.replace(/\.[^.]+$/, ''),
          url: viewUrl,
          type: file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'image',
          thumbnail: file.type.startsWith('image/') ? viewUrl : null,
          duration: 5,
          size: file.size,
          source: 'upload',
        };

        setMediaItems((prev) => mergeMediaItems(prev, [newItem]));
        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
        toast.success(`${file.name} uploaded!`);

        onMediaSelect?.(newItem);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        console.error(err);
      } finally {
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[file.name];
          return next;
        });
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files =
      e.dataTransfer?.files?.length
        ? e.dataTransfer.files
        : Array.from(e.dataTransfer?.items || [])
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter(Boolean);

    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const removeMedia = (id) => {
    setMediaItems((prev) => prev.filter((item) => item.id !== id));
    toast.success('Media removed from library');
  };

  const handleMediaDragStart = (e, item) => {
    e.dataTransfer.setData('application/json', JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg-secondary)' }}>
      <div className="px-4 py-3 border-b shrink-0" style={{ borderColor: 'var(--glass-border)' }}>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Media Library
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-full tabular-nums"
              style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}
              data-testid="media-library-count"
            >
              {filteredItems.length}
              {filteredItems.length !== allItems.length ? (
                <span className="opacity-75">/{allItems.length}</span>
              ) : null}
            </span>
            {stockLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
            ) : null}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-lg gap-1.5"
              style={{ background: 'var(--primary)', color: 'white' }}
              data-testid="upload-media-btn"
            >
              {uploading ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-3 h-3" /> Upload</>
              )}
            </Button>
            {typeof onClose === 'function' ? (
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-white/10 transition"
                style={{ color: 'var(--text-secondary)' }}
                title="Close Media Library"
                aria-label="Close Media Library"
                data-testid="close-media-library-btn"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*,image/*,audio/*"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>

        <div
          className="flex flex-wrap items-center gap-2 pb-1"
          style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 10, marginTop: 4 }}
        >
          <Filter className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-[118px] text-xs shrink-0" data-testid="media-library-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="music">Music / BG</SelectItem>
            </SelectContent>
          </Select>
          <Select value={originFilter} onValueChange={setOriginFilter}>
            <SelectTrigger className="h-8 w-[132px] text-xs shrink-0" data-testid="media-library-origin-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="inhouse_stock">In-house stock</SelectItem>
              <SelectItem value="scene">Scene assets</SelectItem>
              <SelectItem value="final">Final render</SelectItem>
              <SelectItem value="background_music">Background music</SelectItem>
              <SelectItem value="upload">Uploads</SelectItem>
            </SelectContent>
          </Select>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search mood, tone, prompt…"
            className="h-8 px-2 rounded-lg text-xs min-w-[96px] flex-1 border outline-none"
            style={{
              borderColor: 'var(--glass-border)',
              color: 'var(--text-primary)',
              background: 'var(--bg-primary)',
            }}
            data-testid="media-library-search"
          />
        </div>
        {stockItems.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="stock-audio-quick-filters">
            <button
              type="button"
              className="text-[10px] px-2 py-1 rounded-md border"
              style={{
                borderColor: originFilter === 'inhouse_stock' ? 'var(--primary)' : 'var(--glass-border)',
                color: originFilter === 'inhouse_stock' ? 'var(--primary)' : 'var(--text-secondary)',
                background: originFilter === 'inhouse_stock' ? 'var(--primary-light)' : 'transparent',
              }}
              onClick={() => {
                setOriginFilter('inhouse_stock');
                setTypeFilter('all');
              }}
            >
              Stock library ({stockItems.length})
            </button>
            <button
              type="button"
              className="text-[10px] px-2 py-1 rounded-md border"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
              onClick={() => {
                setOriginFilter('inhouse_stock');
                setTypeFilter('music');
              }}
            >
              Stock BG music
            </button>
            <button
              type="button"
              className="text-[10px] px-2 py-1 rounded-md border"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
              onClick={() => {
                setOriginFilter('inhouse_stock');
                setTypeFilter('audio');
              }}
            >
              Stock SFX
            </button>
          </div>
        ) : null}

        {Object.keys(uploadProgress).length > 0 && (
          <div className="space-y-1 mt-2">
            {Object.entries(uploadProgress).map(([name, progress]) => (
              <div key={name} className="text-xs flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--primary)' }} />
                <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>
                  {name}
                </span>
                <span style={{ color: 'var(--text-tertiary)' }}>{progress}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {allItems.length === 0 && (
        <div
          ref={dropZoneRef}
          className="m-4 p-8 border-2 border-dashed rounded-xl transition-all cursor-pointer shrink-0"
          style={{
            borderColor: isDragActive ? 'hsl(var(--primary))' : 'var(--glass-border)',
            background: isDragActive ? 'var(--primary-light)' : 'transparent',
          }}
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="media-drop-zone"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <Upload className="w-10 h-10" style={{ color: 'var(--text-tertiary)' }} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Drop files here or click to upload
              </p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                Supports videos, images, and audio. Generated BG music and SFX also appear in In-house stock.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {allItems.length > 0 && filteredItems.length === 0 ? (
          <p className="text-xs text-center py-8" style={{ color: 'var(--text-tertiary)' }}>
            No media matches these filters. Adjust filters or search.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="relative group rounded-xl overflow-hidden cursor-grab active:cursor-grabbing"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
                draggable
                onDragStart={(e) => handleMediaDragStart(e, item)}
                title={item.prompt || item.name}
                data-testid={`media-item-${item.id}`}
              >
                <div className="aspect-video flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
                  {item.type === 'video' ? (
                    item.thumbnail ? (
                      <img src={item.thumbnail} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <Video className="w-8 h-8" style={{ color: 'var(--text-tertiary)' }} />
                    )
                  ) : item.type === 'audio' ? (
                    <Volume2 className="w-8 h-8" style={{ color: '#ec4899' }} />
                  ) : item.type === 'music' ? (
                    <Music className="w-8 h-8" style={{ color: '#a855f7' }} />
                  ) : (
                    <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                  )}
                </div>

                <div className="p-2">
                  <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {item.name}
                  </p>
                  <p className="text-[10px] truncate" style={{ color: 'var(--text-tertiary)' }}>
                    {item.type === 'video' ? (
                      <Video className="w-2.5 h-2.5 inline mr-1" />
                    ) : item.type === 'music' ? (
                      <Music className="w-2.5 h-2.5 inline mr-1" style={{ color: '#a855f7' }} />
                    ) : item.type === 'audio' ? (
                      <Volume2 className="w-2.5 h-2.5 inline mr-1" />
                    ) : (
                      <ImageIcon className="w-2.5 h-2.5 inline mr-1" />
                    )}
                    {item.size ? `${(item.size / (1024 * 1024)).toFixed(1)} MB` : formatOriginLabel(item)}
                  </p>
                  {item.source === 'inhouse_stock' && item.prompt ? (
                    <p className="text-[9px] mt-0.5 line-clamp-2" style={{ color: 'var(--text-tertiary)' }}>
                      {item.prompt}
                    </p>
                  ) : null}
                </div>

                {item.source !== 'inhouse_stock' ? (
                  <button
                    type="button"
                    className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: '#ef444480' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMedia(item.id);
                    }}
                    data-testid={`remove-media-${item.id}`}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                ) : null}

                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: '#00000040' }}
                >
                  <span
                    className="text-[10px] font-medium text-white px-2 py-1 rounded text-center leading-tight max-w-[95%]"
                    style={{ background: 'var(--primary)' }}
                  >
                    {dragHint(item)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {allItems.length > 0 && (
        <div
          className="px-4 py-2 border-t shrink-0 text-[10px] text-center"
          style={{ borderColor: 'var(--glass-border)', color: 'var(--text-tertiary)' }}
        >
          Drag stock BG music to the purple music lane, stock SFX to the SFX lane — or Upload for more.
        </div>
      )}
    </div>
  );
}
